import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import type { RecentDailyRoutine } from '../../application/dto/DailyRoutineDetails';
import type { DailyMissionsClient } from '../../application/services/DailyMissionsService';
import { ActivityCalendar } from '../components/ActivityCalendar';
import { Button } from '../components/Button';
import { CircularProgress } from '../components/CircularProgress';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { PageHeader } from '../components/PageHeader';
import { useTranslation } from '../design-system/data/translations';
import { MODE_LABELS } from '../design/modeCopy';
import { ChevronIcon } from '../icons';
import { MODE_ICONS } from '../icons/maps';

function readableDate(logicalDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(`${logicalDate}T00:00:00.000Z`));
}

export function HistoryPage({ client }: { readonly client: DailyMissionsClient }) {
  const { t, locale } = useTranslation();
  const [routines, setRoutines] = useState<readonly RecentDailyRoutine[]>();
  const [error, setError] = useState<string>();
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const load = useCallback(async () => {
    setError(undefined);
    const result = await client.listRecent();
    if (result.ok) setRoutines(result.value);
    else setError(result.error.message);
  }, [client]);

  useEffect(() => {
    let active = true;
    void client.listRecent().then((result) => {
      if (!active) return;
      if (result.ok) setRoutines(result.value);
      else setError(result.error.message);
    });
    return () => {
      active = false;
    };
  }, [client]);

  if (error)
    return (
      <main className="page page--center">
        <ErrorState message={error} onRetry={() => void load()} />
      </main>
    );
  if (!routines)
    return (
      <main className="page page--center">
        <LoadingState label={t('history.loading')} />
      </main>
    );

  return (
    <main className="page history-page">
      <PageHeader
        eyebrow={t('history.eyebrow').replace('{count}', String(routines.length))}
        title={t('history.title')}
        description={t('history.description')}
        aside={
          routines.length > 0 ? (
            <div className="view-toggle" role="group" aria-label={t('history.view_aria')}>
              <Button
                variant={view === 'list' ? 'secondary' : 'quiet'}
                onClick={() => setView('list')}
                aria-pressed={view === 'list'}
              >
                {t('history.list')}
              </Button>
              <Button
                variant={view === 'calendar' ? 'secondary' : 'quiet'}
                onClick={() => setView('calendar')}
                aria-pressed={view === 'calendar'}
              >
                {t('history.calendar')}
              </Button>
            </div>
          ) : undefined
        }
      />
      {routines.length === 0 ? (
        <EmptyState title={t('history.empty_title')} message={t('history.empty_message')} />
      ) : view === 'calendar' ? (
        <ActivityCalendar routines={routines} />
      ) : (
        <section className="history-list" aria-label={t('history.section_aria')}>
          {routines.map(({ routine, progress }) => {
            const ModeIcon = MODE_ICONS[routine.dayMode];
            return (
              <Link className="history-row" key={routine.id} to={`/history/${routine.logicalDate}`}>
                <CircularProgress
                  value={progress.percentage}
                  size={40}
                  strokeWidth={4}
                  label={t('history.progress_aria').replace(
                    '{date}',
                    readableDate(routine.logicalDate, locale),
                  )}
                />
                <div className="history-row__date">
                  <strong>{readableDate(routine.logicalDate, locale)}</strong>
                  <span className="history-row__mode">
                    <ModeIcon className="history-row__mode-icon" />
                    {MODE_LABELS[routine.dayMode]} ·{' '}
                    {routine.status === 'completed' ? t('history.completed') : t('history.open')}
                  </span>
                </div>
                <small className="history-row__count">
                  {progress.completedCount}/{progress.activeCount}
                </small>
                <ChevronIcon className="history-row__arrow" aria-hidden="true" />
              </Link>
            );
          })}
        </section>
      )}
    </main>
  );
}
